import React, { useEffect, useState } from 'react';
import { CheckCircle2, ScanLine, BrainCircuit, CloudLightning } from 'lucide-react';

interface AnalyzingViewProps {
  imageSrc: string;
}

export const AnalyzingView: React.FC<AnalyzingViewProps> = ({ imageSrc }) => {
  const [stage, setStage] = useState(0);
  const [progress, setProgress] = useState(0);

  // Simulate progress steps
  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), 800),
      setTimeout(() => setStage(2), 1800),
      setTimeout(() => setStage(3), 2800),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  // Progress Bar Animation
  useEffect(() => {
    const duration = 4000; 
    const interval = 50;
    const steps = duration / interval;
    let currentStep = 0;

    const timer = setInterval(() => {
        currentStep++;
        const newProgress = Math.min((currentStep / steps) * 100, 95); 
        setProgress(newProgress);
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const steps = [
    { label: "Uploading image...", icon: <UploadIcon /> },
    { label: "Scanning object features...", icon: <ScanLine className="w-5 h-5" /> },
    { label: "Identifying category...", icon: <BrainCircuit className="w-5 h-5" /> },
    { label: "Generating viral copy...", icon: <CloudLightning className="w-5 h-5" /> },
  ];

  return (
    <div className="flex flex-col h-full p-6 items-center justify-center text-center animate-in zoom-in-95 duration-500">
      
      {/* Scanner Frame */}
      <div className="relative w-64 h-64 rounded-3xl overflow-hidden mb-8 shadow-2xl shadow-blue-500/20 border-2 border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
        <img src={imageSrc} alt="Analyzing" className="w-full h-full object-cover opacity-60 dark:opacity-50" />
        
        {/* Scanning Line Effect */}
        <div className="absolute inset-x-0 h-1 bg-blue-500 shadow-[0_0_15px_3px_rgba(59,130,246,0.6)] animate-scan z-10"></div>
        <div className="absolute inset-0 bg-blue-600/10 animate-pulse z-0"></div>
        
        {/* Grid Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:20px_20px] opacity-20"></div>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Analyzing Product...</h2>
      
      {/* Progress Bar */}
      <div className="w-full max-w-xs bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-8 overflow-hidden border border-gray-300 dark:border-gray-600">
        <div 
            className="bg-gradient-to-r from-blue-600 to-blue-400 h-2 rounded-full transition-all duration-100 ease-out shadow-[0_0_10px_rgba(59,130,246,0.5)]"
            style={{ width: `${progress}%` }}
        ></div>
      </div>

      <div className="space-y-4 w-full max-w-xs text-left">
        {steps.map((s, idx) => (
          <div key={idx} className={`flex items-center gap-3 transition-all duration-500 ${idx <= stage ? 'opacity-100 translate-x-0' : 'opacity-30 -translate-x-4'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 
                ${idx < stage ? 'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400' : 
                  idx === stage ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400' : 
                  'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'}`}>
                {idx < stage ? <CheckCircle2 className="w-5 h-5" /> : s.icon}
            </div>
            <span className={`text-sm font-medium ${idx <= stage ? 'text-gray-900 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}`}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const UploadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>
);